//	Testing against live Dodo on staging, and reverting it: see docs/code/dodo-live-on-staging.md.

import { useState, useEffect } from 'react'
import { useUserStore } from '@/state/user-store'
import { fetchUser } from '@/state/user-actions'
import { useSessionContext } from '@/state/session-context'
import { Button } from '@/components/ui/button'
import { toast } from 'react-hot-toast'
import { getAccountService } from '@/pages/chat/feathers-client'
import { DodoPayments, CheckoutEvent } from "dodopayments-checkout"
import { useNavigate } from 'react-router-dom'
import { isProduction } from '@/env'

export function SubscriptionSection() {
	const { session } = useSessionContext()
	const user = useUserStore(state => state.user)
	const [isLoading, setIsLoading] = useState(false)
	const [dodoPayments, setDodoPayments] = useState<typeof DodoPayments | null>(null)
	const navigate = useNavigate()
	const accountService = getAccountService()

	useEffect(() => {
		const initDodo = async () => {
			if (dodoPayments)
				return

			try {
				const mode = isProduction ? "live" : "test"

				DodoPayments.Initialize({
					mode: mode,
					onEvent: (event: CheckoutEvent) => {
						console.log('Dodo event:', event.event_type, event)

						if (event.event_type === 'checkout.error') {
							const errorMessage = event.data?.message
							if (typeof errorMessage === 'string') {
								toast.error(errorMessage)
							} else {
								toast.error('Payment error occurred')
							}
						}
					}
				})

				setDodoPayments(DodoPayments)
			} catch (error) {
				console.error('Failed to initialize Dodo Payments:', error)
				toast.error('Failed to initialize payment system')
			}
		}

		initDodo()
	}, [session?.userId, dodoPayments])

	useEffect(() => {
		const params = new URLSearchParams(window.location.search)
		const checkoutParam = params.get('checkout')
		const statusParam = params.get('status')
		const paymentId = params.get('payment_id')

		if (checkoutParam === 'success' && session?.userId) {
			//	fire-and-forget: fetchUser may throw on transient failure (see user-actions.ts).
			//	attach a .catch() so we don't produce an unhandled promise rejection.
			//	nothing else to do on failure — the user can reload to refresh their subscription state.
			if (statusParam === 'succeeded') {
				toast.success('Payment successful! Updating your subscription...')
				fetchUser(session.userId)
					.catch(err => console.warn('post-checkout fetchUser failed (transient):', err)) /*noawait*/
			} else if (statusParam === 'failed') {
				toast.error('Payment failed. Please try again.')
			} else if (paymentId && !statusParam) {
				toast.loading('Processing payment...')
				fetchUser(session.userId)
					.catch(err => console.warn('post-checkout fetchUser failed (transient):', err)) /*noawait*/
			}

			const url = new URL(window.location.href)
			url.searchParams.delete('checkout')
			url.searchParams.delete('status')
			url.searchParams.delete('payment_id')
			window.history.replaceState({}, '', url.toString())
		}
	}, [session?.userId])

	const handleSubscribe = async () => {
		if (!session?.userId) {
			toast.error('Please log in to subscribe')
			return
		}

		if (!dodoPayments) {
			toast.error('Payment system not ready, please try again')
			return
		}

		setIsLoading(true)

		try {
			const result = await accountService.createCheckoutSession({})

			if (!result.success) {
				toast.error(result.message || 'Failed to create checkout session')
				setIsLoading(false)
				return
			}

			if (dodoPayments) {
				dodoPayments.Checkout.open({ checkoutUrl: result.checkoutUrl })
			}
		} catch (error) {
			console.error('Failed to create checkout session:', error)
			toast.error('Failed to start checkout process')
		} finally {
			setIsLoading(false)
		}
	}

	const handleManageSubscription = async () => {
		if (!session?.userId) {
			toast.error('Please log in to manage subscription')
			return
		}

		setIsLoading(true)

		try {
			const accountService = getAccountService()
			const result = await accountService.createCustomerPortalSession({})

			if (!result.success) {
				toast.error(result.message || 'Failed to open customer portal')
				setIsLoading(false)
				return
			}

			window.open(result.portalUrl, '_blank')

			// we open the customer portal in a new tab, and won't get notifications about 
			// the user having made changes, so would keep showing stale data.
			// instead, we just close the settings page. 
			// if the user re-opens it, that will trigger a re-fetch of the user's data, including the subscription status.
			// the user may still re-open settings _before_ making changes, for now we just ignore this edge case. 
			navigate('/chat')
		} catch (error) {
			console.error('Failed to open customer portal:', error)
			toast.error('Failed to open customer portal')
		} finally {
			setIsLoading(false)
		}
	}

	if (!user)
		return <p className="text-sm text-gray-500">Loading...</p>

	const status = user.accountStatus || 'trial'
	const hasActiveSubscription = status === 'paid'
	const canSubscribe = user.canSubscribe !== false

	const getStatusDisplay = () => {
		if (hasActiveSubscription) {
			return (
				<div className="space-y-3">
					<div>
						<p className="text-sm font-medium text-gray-900">Current Plan: Premium</p>
						{/* <p className="text-sm text-gray-600 mt-1">
							You have unlimited access to all features.
						</p> */}
					</div>
					<div className="bg-green-50 border border-green-200 rounded-md p-3">
						<p className="text-sm text-green-800">
							Your subscription is active. Thank you for your support!
						</p>
					</div>
					<Button
						onClick={handleManageSubscription}
						disabled={isLoading}
						variant="outline"
						className="w-full sm:w-auto"
					>
						{isLoading ? 'Loading...' : 'Manage Subscription'}
					</Button>
				</div>

			)
		} else if (canSubscribe) {
			return (
				<div className="space-y-3">
					<div>
						<p className="text-sm font-medium text-gray-900">Current Plan: Free</p>
					</div>
					<div className="bg-gray-50 border border-gray-200 rounded-md p-3">
						<p className="text-sm text-gray-800 mb-3">By subscribing:</p>
						<ul className="list-disc pl-4 space-y-1 text-sm">
							<li>Get more groups (than free users in your area).</li>
							<li>Get bragging rights about how you help us keep the lights on.</li>
							<li>More to come!</li>
						</ul>
					</div>
					<Button
						onClick={handleSubscribe}
						disabled={isLoading || !dodoPayments}
						className="w-full sm:w-auto"
					>
						{isLoading ? 'Loading...' : 'Subscribe (USD 8/month)'}
					</Button>
				</div>
			)
		} else {
			return (
				<div className="space-y-3">
					<div>
						<p className="text-sm font-medium text-gray-900">Current Plan: Waitlist</p>
					</div>
					<div className="bg-gray-50 border border-gray-200 rounded-md p-3">
						<p className="text-sm text-gray-800">
							Subscriptions are not yet available in your area.
							We'll enable them once there are enough users near you.
						</p>
					</div>
				</div>
			)
		}
	}

	return (
		<div className="space-y-4">
			{getStatusDisplay()}
		</div>
	)
}
